import React from "react";
import { Accordion, AttachmentList, toast } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { FormHelperText, Typography } from "@mui/material";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import { Stack } from "components/layout/reusableLayouts";
import { toAttachmentItems } from "utils/attachments/attachmentItems";
import {
  useAttachmentListLabels,
  useAttachmentViewerLabels,
} from "hooks/useAttachmentLabels";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import {
  RECEIPT_FILE_ACCEPT,
  partitionReceiptFiles,
} from "utils/receipts/receiptFileTypes";

interface AttachmentsSectionProps {
  attachments: LocalImageAsset[];
  receiptRequired: boolean;
  canUploadToOcr: boolean;
  maxReceipts: number;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onAddAssets: (files: LocalImageAsset[]) => void;
  onRemove: (assetId: string) => void;
  onOcrResult: (result: unknown) => void;
  /** Validation message rendered under the section (empty → nothing). */
  errorText?: React.ReactNode;
}

/**
 * Collapsible "Attachments" section: the DS attachment rows, with a dropzone
 * until `maxReceipts`, fed by the add and OCR pipeline in `AttachmentsComponent`.
 */
const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  attachments,
  receiptRequired,
  canUploadToOcr,
  maxReceipts,
  expanded,
  onExpandedChange,
  onAddAssets,
  onRemove,
  onOcrResult,
  errorText,
}) => {
  const { t, i18n } = useTranslation();

  // Reject unsupported file types before they reach the add + OCR pipeline.
  // The `accept` attribute only filters the click picker; drag-drop bypasses
  // it, so we validate here (mirrors the receipt library's upload flow).
  const attachmentLabels = useAttachmentListLabels();
  const viewerLabels = useAttachmentViewerLabels();

  const acceptSupportedFiles = (
    raw: FileList,
    onFiles: (files: FileList) => void | Promise<void>,
  ) => {
    const { supported, rejected } = partitionReceiptFiles(raw);

    if (rejected.length > 0) {
      // Build the rejected-file list with `Intl.ListFormat` so the conjunction
      // is localized, then interpolate it into a single translatable sentence
      // (rather than concatenating translated fragments, which bakes in
      // English word order).
      const files = new Intl.ListFormat(i18n.language, {
        style: "long",
        type: "conjunction",
      }).format(rejected);
      toast.error(
        rejected.length > 1
          ? t("UnsupportedFileTypesMessage", { files })
          : t("UnsupportedFileTypeMessage", { file: rejected[0] }),
      );
    }

    if (supported.length === 0) return;

    const detached = new DataTransfer();
    supported.forEach((file) => detached.items.add(file));
    // `onFiles` runs the async add + OCR pipeline; catch so a rejection there
    // doesn't surface as an unhandled promise rejection.
    Promise.resolve(onFiles(detached.files)).catch((error) => {
      console.error("Failed to process attachments:", error);
      toast.error(t("Failed to upload receipts"));
    });
  };

  return (
    <Stack gap="0">
      <Accordion
        title={
          <>
            {t("Attachments")}
            {receiptRequired && (
              <Typography
                component="span"
                variant="inherit"
                sx={{
                  color: "var(--color-text-danger)",
                  ml: "var(--space-1)",
                }}
              >
                *
              </Typography>
            )}
          </>
        }
        expanded={expanded}
        onChange={(_, next) => onExpandedChange(next)}
      >
        <AttachmentsComponent
          renderType={AttachmentRenderType.ACCORDION}
          drawerType={AttachmentDrawerType.ATTACHMENT}
          localAssets={attachments}
          onAddAssets={onAddAssets}
          onRemoveAssets={onRemove}
          uploadFileToOcr={canUploadToOcr}
          onOcrResult={onOcrResult}
          // One list, not a dropzone-or-list branch: AttachmentList renders the
          // files and its dropzone together, so the empty and populated cases are
          // the same component, and `maxFiles` states the limit once.
          renderDropZone={(onFiles, scanningFile) => (
            <AttachmentList
              files={toAttachmentItems(attachments, t("Receipt")).map(
                (item, index) =>
                  scanningFile && attachments[index].file === scanningFile
                    ? { ...item, pending: t("Scanning") }
                    : item,
              )}
              accept={RECEIPT_FILE_ACCEPT}
              multiple
              maxFiles={maxReceipts}
              hint={t(
                "JPG, PDF, PNG, WEBP  • Max 10MB • Up to {{count}} receipts",
                { count: maxReceipts },
              )}
              labels={attachmentLabels}
              viewer={{ labels: viewerLabels, filmstrip: true }}
              // Drag-drop bypasses `accept`, so the validation stays ours.
              onAdd={(picked: File[]) => {
                const transfer = new DataTransfer();
                picked.forEach((file) => transfer.items.add(file));
                acceptSupportedFiles(transfer.files, onFiles);
              }}
              onRemove={onRemove}
            />
          )}
        />
      </Accordion>
      {errorText && (
        <FormHelperText error sx={{ mx: 0 }}>
          {errorText}
        </FormHelperText>
      )}
    </Stack>
  );
};

export default AttachmentsSection;
